import { API_BASE_URL } from "../config";

export type CredEntry = {
  source: string;
  secret_types: string[];
  value_hints?: Record<string, string>;
};

export type OrgCredsResponse = {
  organization: { short_name: string; name: string };
  can_manage: boolean;
  organization_credential_management_enabled?: boolean;
  credentials: CredEntry[];
};

export type UserCredsResponse = {
  user?: { id: number; username: string; email: string };
  credentials: CredEntry[];
};

// Global default credentials: keyed by settings-secret name + raw settings keys.
export type GlobalCredEntry = {
  name: string;
  keys: string[];
  value_hints?: Record<string, string>;
};

export type GlobalCredsResponse = {
  product: string;
  credentials: GlobalCredEntry[];
  warning?: string;
};

// ---------------------------------------------------------------------------
// User self-service credentials
// ---------------------------------------------------------------------------

export const fetchUserCreds = async (accessToken: string, userId: string): Promise<UserCredsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/user/${encodeURIComponent(userId)}/credentials`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || "Unable to load credentials.");
  }
  return res.json();
};

export const setUserCreds = async (
  accessToken: string,
  userId: string,
  source: string,
  fields: Record<string, string>,
): Promise<{ source: string; secret_types: string[] }> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/user/${encodeURIComponent(userId)}/credentials/${source}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || "Unable to save credentials.");
  }
  return res.json();
};

export const deleteUserCreds = async (
  accessToken: string,
  userId: string,
  source: string,
): Promise<{ source: string; removed_secret_types: string[] }> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/user/${encodeURIComponent(userId)}/credentials/${source}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error("Unable to remove credentials.");
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// Org credentials (any org by short_name)
// ---------------------------------------------------------------------------

export const fetchOrgCreds = async (
  accessToken: string,
  shortName: string,
): Promise<OrgCredsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/orgs/${shortName}/credentials`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error("Unable to load org credentials.");
  }
  return res.json();
};

export const setOrgCreds = async (
  accessToken: string,
  shortName: string,
  source: string,
  fields: Record<string, string>,
): Promise<{ source: string; secret_types: string[] }> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/orgs/${shortName}/credentials/${source}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fields),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || "Unable to save org credentials.");
  }
  return res.json();
};

export const deleteOrgCreds = async (
  accessToken: string,
  shortName: string,
  source: string,
): Promise<{ source: string; removed_secret_types: string[] }> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/admin/orgs/${shortName}/credentials/${source}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!res.ok) {
    throw new Error("Unable to remove org credentials.");
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// Global default credentials (staff-only)
// ---------------------------------------------------------------------------

export type GlobalProductsResponse = {
  products: string[];
};

export const fetchGlobalProducts = async (accessToken: string): Promise<GlobalProductsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/global/products`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 403) {
    throw new Error("Staff access required.");
  }
  if (!res.ok) {
    throw new Error("Unable to load products.");
  }
  return res.json();
};

export const fetchGlobalCreds = async (accessToken: string, product: string): Promise<GlobalCredsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/global/${encodeURIComponent(product)}/credentials`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 403) {
    throw new Error("Staff access required.");
  }
  if (!res.ok) {
    throw new Error("Unable to load global credentials.");
  }
  return res.json();
};

export const setGlobalCreds = async (
  accessToken: string,
  product: string,
  name: string,
  fields: Record<string, string>,
): Promise<{ product: string; name: string; keys: string[] }> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/global/${encodeURIComponent(product)}/credentials/${encodeURIComponent(name)}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fields),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || "Unable to save global credentials.");
  }
  return res.json();
};

export const CREDENTIAL_SECRET_TYPE_LABELS: Record<string, string> = {
  api_key: "API Key",
  username: "Username",
  password: "Password",
  client_id: "Client ID",
  client_secret: "Client Secret",
};

export type CredentialSource = {
  code: string;
  name: string;
  member_code?: string;
};

export type CredentialSourcesResponse = {
  sources: CredentialSource[];
};

export const fetchCredentialSources = async (accessToken: string): Promise<CredentialSourcesResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/credential-sources`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error("Unable to load credential sources.");
  }
  return res.json();
};

export const deleteGlobalCreds = async (
  accessToken: string,
  product: string,
  name: string,
): Promise<{ product: string; name: string; deleted: boolean }> => {
  const res = await fetch(
    `${API_BASE_URL}/api/v1/global/${encodeURIComponent(product)}/credentials/${encodeURIComponent(name)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!res.ok) {
    throw new Error("Unable to remove global credentials.");
  }
  return res.json();
};
